/** Launcher offset (px) from the anchored corner. Shared by the in-DOM shell and
 *  the iframe loader contract (`wexio:widget:offset:v1`). */
export interface LauncherOffset {
  x: number;
  y: number;
}

/** Hard cap per axis so a bad value can never push the widget off-screen. */
export const MAX_LAUNCHER_OFFSET = 200;

function clampAxis(v: unknown): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.min(MAX_LAUNCHER_OFFSET, Math.max(0, Math.round(n)));
}

/** Normalise an untrusted offset: each axis rounded and clamped to 0..200, a
 *  non-finite axis counts as 0. `undefined` when `raw` is not an object. */
export function clampLauncherOffset(raw: unknown): LauncherOffset | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const { x, y } = raw as { x?: unknown; y?: unknown };
  return { x: clampAxis(x), y: clampAxis(y) };
}

function hostAxis(v: unknown): number | undefined {
  if (v === undefined || v === null || v === "") return undefined;
  return Number.isFinite(Number(v)) ? clampAxis(v) : undefined;
}

/** Precedence PER AXIS (as the iframe loader): a host axis (a finite number) > `theme.launcherOffset` axis > 0.
 *  A host that set only `x` keeps the configured `y`, and the reverse. */
export function resolveLauncherOffset(
  host: Partial<LauncherOffset> | undefined,
  config: LauncherOffset | undefined,
): LauncherOffset {
  const cfg = clampLauncherOffset(config);
  const h = host && typeof host === "object" ? host : undefined;
  return { x: hostAxis(h?.x) ?? cfg?.x ?? 0, y: hostAxis(h?.y) ?? cfg?.y ?? 0 };
}
